import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { AutoComplete } from 'primeng/autocomplete';
import { TableLazyLoadEvent } from 'primeng/table';
import { TranslateService } from '@ngx-translate/core';

import { ContadorService } from '../../../services/contador/contador.service';
import { ContadorContextoService } from '../../../services/contador/contador-contexto.service';
import { ContadorOperarService } from '../../../services/contador/contador-operar.service';
import { ClienteContadorConsultaDTO } from '../../../models/contador/contador.model';
import { ContaDTO } from '../../../models/gerenciar-contas/conta.model';
import { NotaDTO, LocalizableEnum } from '../../../models/nota.model';
import { TipoPadraoNota } from '../../../enums/TipoPadraoNota.enum';
import { Moeda, MOEDA_CONFIG, MoedaConfig } from '../../../enums/moeda.enum';
import { ExtratoDeCaixaPdfParams } from '../../../models/movimento/extrato-de-caixa-pdf.model';
import { PeriodoFiltro, TipoPeriodo } from '../../../models/dashboard/periodo-filtro.model';
import { FileUtils } from '../../../shared/utils/file-utils';
import { EstadoNotaEletronica } from '../../../enums/EstadoNotaEletronica.enum';
import {
  EstadoNotaEletronicaOpcaoFormDTO,
  ESTADOS_NOTA_ELETRONICA_FILTRO,
  FILTRO_NAO_ELETRONICA,
  FILTRO_NAO_EMITIDA,
} from '../../../models/contador/contador-operar.model';
import { calcularPeriodoPredefinido, fmtDateTime } from '../../../shared/utils/periodo.utils';
import { Subscription } from 'rxjs';

/**
 * Tela "Operar Cliente" (/contador/operar).
 *
 * Apos selecionar um cliente (indicacao CONFIRMADA), o contador opera sobre os
 * dados do cliente reusando endpoints existentes: extrato de caixa (PDF),
 * listagem de notas e emissao (transmissao) de NFe. O acesso e habilitado
 * server-side por requisicao pelo header `X-Empresa-Contexto`, injetado pelo
 * `ContadorContextoInterceptor` enquanto ha cliente ativo no
 * `ContadorContextoService`.
 *
 * Este componente e o UNICO dono do ciclo de vida do contexto: ativa ao
 * selecionar, limpa em ngOnDestroy / troca / 403 de contexto.
 *
 * Fonte da verdade: `vendas/docs/api/API_CONTRACT_contador-operar-cliente.md`
 * e `DESIGN_FE_contador-operar-cliente.md`.
 */
@Component({
  selector: 'app-operar-cliente',
  templateUrl: './operar-cliente.component.html',
  styleUrls: ['./operar-cliente.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class OperarClienteComponent implements OnInit, OnDestroy {
  // ===========================================================================
  // Seletor de cliente
  // ===========================================================================

  clientes: ClienteContadorConsultaDTO[] = [];
  carregandoClientes = true;
  erroClientes = false;

  /** Cliente atualmente selecionado (null = mostra apenas o card de cliente). */
  clienteSelecionado: ClienteContadorConsultaDTO | null = null;

  /** Referencia do autocomplete do seletor (para o clique na lupa abrir a lista). */
  @ViewChild('clienteAc') clienteAc?: AutoComplete;

  /**
   * Valor ligado ao `p-autoComplete` do seletor. Espelha `clienteSelecionado`,
   * mas e um estado separado porque o autocomplete tambem guarda o texto digitado
   * enquanto o usuario busca (antes de escolher um item).
   */
  clienteAutoComplete: ClienteContadorConsultaDTO | null = null;

  /** Sugestoes exibidas no dropdown do autocomplete (filtro em memoria). */
  clientesFiltrados: ClienteContadorConsultaDTO[] = [];

  /**
   * Aba ativa do `p-tabView` (0 = primeira). Pode ser definida pelo query param
   * `aba` ao abrir a tela (ex.: navegacao a partir de um titulo de despesa
   * acessoria abre direto em "Contrato e Despesas"). Default = primeira aba.
   */
  abaAtivaIndex = 0;

  /**
   * Sub-aba dentro de "Extrato + Notas": 0 = nova venda, 1 = listagem de notas.
   * Padrão exibe a listagem (índice 1); a "Nova venda" fica à esquerda, no mesmo
   * padrão das demais abas (registro/cadastro primeiro, listagem depois).
   */
  subAbaExtratoIndex = 1;

  /**
   * Sub-aba ativa dentro da aba "Artículos": 0 = Artículos (itens),
   * 1 = Categorías, 2 = Subcategorías, 3 = Marcas, 4 = Unidades. Começa em 0
   * (itens) — a listagem/registro de itens é o uso mais comum.
   */
  subAbaArticulosIndex = 0;

  /**
   * Mapa de identificadores ESTAVEIS de aba -> indice no `p-tabView`. Evita
   * indice numerico magico nas URLs; deve ser mantido em sincronia com a ordem
   * dos `<p-tabPanel>` no template. A "nova-venda" virou sub-aba de "extrato-notas"
   * (aba 3, sub-aba 1) e é tratada à parte no `ngOnInit`.
   */
  private readonly ABA_INDICE: Record<string, number> = {
    'contratos-gastos': 0,
    'gastos-acessorios': 1,
    configuracao: 2,
    'extrato-notas': 3,
    timbrado: 4,
    itens: 5,
    clientes: 6,
    fornecedores: 7,
  };

  // ===========================================================================
  // Extrato de caixa
  // ===========================================================================

  contas: ContaDTO[] = [];
  carregandoContas = false;
  contaSelecionadaId: number | null = null;
  gerandoPdf = false;

  /** Periodo escolhido no app-periodo-filtro (dataInicio/dataFim ja formatados). */
  private periodo: PeriodoFiltro | null = null;

  // ===========================================================================
  // Notas (tabela lazy)
  // ===========================================================================

  notas: NotaDTO[] = [];
  first = 0;
  rows = 10;
  totalRecords = 0;
  carregandoNotas = false;
  erroNotas = false;

  /** Opcoes de itens por pagina (mesma referencia da Lista de Vendas). */
  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  /** Valor anterior de "rows" usado como guarda no onRowsPerPageChangeNotas
   *  para evitar recarga duplicada quando o (ngModelChange) do dropdown de
   *  itens por pagina dispara no bootstrap com o valor atual. */
  private _lastRowsNotas = 10;

  /** Assinatura da ultima carga de notas (cliente|page|size|periodo|estado).
   *  Usada para ignorar a 2a chamada IDENTICA consecutiva — caso da primeira
   *  selecao de cliente, em que o onLazyLoad (init do p-table) e o
   *  onPeriodoChanged (emissao inicial do app-periodo-filtro) disparam juntos.
   *  E zerada no erro para nao bloquear o botao "tentar novamente". */
  private _ultimaAssinaturaNotas: string | null = null;

  /** Campo de ordenacao atual (campo do backend); null = sem ordenacao (default
   *  do backend). Setado ao clicar num cabecalho ordenavel da tabela de notas. */
  private sortField: string | null = null;

  /** Direcao da ordenacao: 1 = asc, -1 = desc, 0 = sem ordenacao. */
  private sortOrder = 0;

  /**
   * Estado da nota eletronica selecionado no filtro: null = "Todos"; um dos 9
   * estados; [[FILTRO_NAO_EMITIDA]] = eletronica ainda nao emitida; ou
   * [[FILTRO_NAO_ELETRONICA]] = fatura legal nao eletronica.
   */
  estadoSelecionado:
    | EstadoNotaEletronica
    | typeof FILTRO_NAO_EMITIDA
    | typeof FILTRO_NAO_ELETRONICA
    | null = null;

  /** Opcoes do dropdown de estado (FormDTO { label, value }); label localizado. */
  estadoOptions: EstadoNotaEletronicaOpcaoFormDTO[] = [];

  /** Spinner do botao "Exportar" (Livro de Vendas: PDF / Excel / CSV). */
  gerandoLivro = false;

  /** Itens do menu popup "Exportar" (PDF / Excel .xlsx / CSV). */
  exportarLivroItens: MenuItem[] = [];

  /** Id da nota cuja NFe esta sendo emitida (para [loading] na linha). */
  emitindoNotaId: number | null = null;

  /** Popup "Estado factura electrónica" (card de NFe) para emitir/transmitir. */
  mostrarDialogEstadoNfe = false;
  notaEstadoNfe: NotaDTO | null = null;

  /** Subscription da troca de idioma (cancelada em ngOnDestroy para evitar leak). */
  private langChangeSub?: Subscription;

  /**
   * Id do cliente (`ClienteContadorConsultaDTO.empresaIndicadaId`) a pre-selecionar
   * ao abrir a tela, recebido via query param `clienteId` (ex.: navegacao a partir
   * de um titulo de despesa acessoria em Contas a Receber). `null` = abre no seletor.
   * Consumido uma unica vez apos a primeira carga de clientes.
   */
  private clienteIdParaPreSelecionar: number | null = null;

  constructor(
    private contadorService: ContadorService,
    private contexto: ContadorContextoService,
    private operarService: ContadorOperarService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  // ===========================================================================
  // Ciclo de vida
  // ===========================================================================

  ngOnInit(): void {
    // Query param opcional `clienteId`: quando presente (ex.: vindo de um titulo
    // de despesa acessoria em Contas a Receber), pre-seleciona o cliente apos a
    // primeira carga da lista. snapshot basta: a tela nao e reaproveitada por
    // mudanca de param sem recriar o componente.
    const clienteIdParam = this.route.snapshot.queryParamMap.get('clienteId');
    const clienteIdNumero = clienteIdParam != null ? Number(clienteIdParam) : NaN;
    this.clienteIdParaPreSelecionar = Number.isFinite(clienteIdNumero) ? clienteIdNumero : null;

    // Query param opcional `aba`: abre direto na aba correspondente (id estavel).
    // Param ausente/invalido => mantem a primeira aba (default), sem erro.
    const abaParam = this.route.snapshot.queryParamMap.get('aba');
    if (abaParam === 'nova-venda') {
      // "nova-venda" agora e sub-aba de "extrato-notas" (aba 3, sub-aba 0).
      this.abaAtivaIndex = this.ABA_INDICE['extrato-notas'];
      this.subAbaExtratoIndex = 0;
    } else if (abaParam != null && this.ABA_INDICE[abaParam] != null) {
      this.abaAtivaIndex = this.ABA_INDICE[abaParam];
    }

    this.carregarClientes();
    this.montarOpcoesEstado();
    this.montarItensExportarLivro();
    // Inicializa (de forma síncrona) o período com "Este mês" para que export e
    // tabela respeitem o recorte mensal sem interação do usuário: o app-periodo-filtro
    // com [persistir]="false" só emite periodoChanged ao abrir o diálogo, então sem
    // isto this.periodo ficaria null no primeiro lazy-load do p-table.
    this.inicializarPeriodoPadrao();
    // Reconstroi os labels do dropdown e do menu de exportar ao trocar de idioma (pt-BR <-> es).
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.montarOpcoesEstado();
      this.montarItensExportarLivro();
    });
  }

  /**
   * Inicializa this.periodo com o preset "thisMonth" usando os mesmos utilitários
   * e labels que o app-periodo-filtro usa internamente. Garante que o export do
   * Livro de Vendas (e a tabela de notas) respeitem o período padrão antes de
   * qualquer interação do usuário com o filtro.
   */
  private inicializarPeriodoPadrao(): void {
    const tipo: TipoPeriodo = 'thisMonth';
    const labels: Record<TipoPeriodo, string> = {
      untilToday: this.translate.instant('dashboard.filtros.ate_hoje'),
      yesterdayAndToday: this.translate.instant('dashboard.filtros.ontem_e_hoje'),
      thisWeek: this.translate.instant('dashboard.filtros.esta_semana'),
      thisMonth: this.translate.instant('dashboard.filtros.este_mes'),
      untilMonthEnd: this.translate.instant('dashboard.filtros.ate_fim_do_mes'),
      prevMonth: this.translate.instant('dashboard.filtros.mes_anterior'),
      nextMonth: this.translate.instant('dashboard.filtros.proximo_mes'),
      last30: this.translate.instant('dashboard.filtros.ultimos_30_dias'),
      today: this.translate.instant('dashboard.filtros.hoje'),
      yesterday: this.translate.instant('dashboard.filtros.ontem'),
      showAll: this.translate.instant('dashboard.filtros.mostrar_todos'),
      custom: '',
    };
    const result = calcularPeriodoPredefinido(tipo, new Date(), labels, 0, 0, 23, 59);
    this.periodo = {
      tipo,
      dataInicio: fmtDateTime(result.start),
      dataFim: fmtDateTime(result.end),
      texto: result.texto,
    };
  }

  /**
   * Monta os itens do menu popup "Exportar" (PDF / Excel / CSV). O clique no
   * botao "Exportar Libro de Ventas" abre este menu; o usuario escolhe o
   * formato, que entao dispara o handler correspondente.
   */
  private montarItensExportarLivro(): void {
    this.exportarLivroItens = [
      {
        label: this.translate.instant('contador_operar.livro.pdf'),
        icon: 'pi pi-file-pdf',
        command: () => this.gerarLivroDeVendas(),
      },
      {
        label: this.translate.instant('contador_operar.livro.excel'),
        icon: 'pi pi-file-excel',
        command: () => this.gerarLivroDeVendasArquivo('xlsx'),
      },
      {
        label: this.translate.instant('contador_operar.livro.csv'),
        icon: 'pi pi-file',
        command: () => this.gerarLivroDeVendasArquivo('csv'),
      },
    ];
  }

  /**
   * Monta as opcoes do dropdown de estado: "Todos" (value null, default) + os 9
   * estados da nota eletronica, com label localizado via i18n.
   */
  private montarOpcoesEstado(): void {
    const todos: EstadoNotaEletronicaOpcaoFormDTO = {
      label: this.translate.instant('contador_operar.filtro_estado.todos'),
      value: null,
    };
    const estados = ESTADOS_NOTA_ELETRONICA_FILTRO.map<EstadoNotaEletronicaOpcaoFormDTO>((estado) => ({
      label: this.translate.instant(`contador_operar.filtro_estado.${estado.toLowerCase()}`),
      value: estado,
    }));
    // Opcao "Nao emitida": fatura do TIPO eletronica ainda nao transmitida ao SIFEN
    // (sem estado eletronico/NULL). Mesmo rotulo do badge da coluna Situacao.
    const naoEmitida: EstadoNotaEletronicaOpcaoFormDTO = {
      label: this.translate.instant('contador_operar.notas.situacao_nao_emitida'),
      value: FILTRO_NAO_EMITIDA,
    };
    // Opcao "Nao eletronica": fatura legal com timbrado fisico (sem estado eletronico/NULL).
    // Mesmo rotulo do badge da coluna Situacao, para que tela e filtro fiquem coerentes.
    const naoEletronica: EstadoNotaEletronicaOpcaoFormDTO = {
      label: this.translate.instant('contador_operar.notas.situacao_nao_eletronica'),
      value: FILTRO_NAO_ELETRONICA,
    };
    this.estadoOptions = [todos, ...estados, naoEmitida, naoEletronica];
  }

  /**
   * Nao limpa o contexto aqui de proposito: ele precisa SOBREVIVER a navegacao
   * para a tela de fatura (`/notas-list/fatura/:id`). A limpeza ao sair do fluxo
   * do contador e centralizada no `ContadorContextoService` (NavigationStart para
   * qualquer rota fora do contexto). Troca de cliente / 403 continuam limpando.
   */
  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }

  // ===========================================================================
  // Seletor de cliente
  // ===========================================================================

  private carregarClientes(page = 0, size = 200): void {
    this.carregandoClientes = true;
    this.erroClientes = false;

    this.contadorService.listarClientes(page, size).subscribe({
      next: (res) => {
        this.clientes = res.content || [];
        this.carregandoClientes = false;
        this.preSelecionarClienteSePedido();
      },
      error: () => {
        this.clientes = [];
        this.erroClientes = true;
        this.carregandoClientes = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('contador_operar.seletor.erro_carregar'),
        });
      },
    });
  }

  recarregarClientes(): void {
    this.carregarClientes();
  }

  /**
   * Filtra a lista de clientes JA carregada (busca em memoria — o backend devolve
   * todos de uma vez). Casa por nome, e-mail ou id da empresa; sem termo, mostra
   * a lista inteira (mesmo comportamento do seletor da tela de Vendas ao focar).
   */
  filtrarClientes(event: { query: string }): void {
    const termo = (event?.query || '').trim().toLowerCase();
    if (!termo) {
      this.clientesFiltrados = [...this.clientes];
      return;
    }
    this.clientesFiltrados = this.clientes.filter(
      (c) =>
        c.nome?.toLowerCase().includes(termo) ||
        c.email?.toLowerCase().includes(termo) ||
        String(c.empresaIndicadaId).includes(termo),
    );
  }

  /** Clique na lupa: foca o input, o que abre a lista completa (completeOnFocus). */
  abrirListaClientes(): void {
    const input = this.clienteAc?.el?.nativeElement?.querySelector('input') as
      | HTMLInputElement
      | undefined;
    input?.focus();
  }

  /**
   * Pre-seleciona o cliente indicado por `clienteId` (query param) apos a lista
   * de clientes carregar. Executa uma unica vez (zera o alvo apos consumir) para
   * nao interferir em recargas manuais. Sem match ou ja consumido: nao faz nada
   * (mantem o seletor vazio, comportamento padrao).
   */
  private preSelecionarClienteSePedido(): void {
    if (this.clienteIdParaPreSelecionar == null) {
      return;
    }
    const alvo = this.clienteIdParaPreSelecionar;
    this.clienteIdParaPreSelecionar = null;
    const cliente = this.clientes.find((c) => c.empresaIndicadaId === alvo);
    if (cliente) {
      this.onSelecionarCliente(cliente);
    }
  }

  /** Ao escolher (ou trocar) um cliente: liga o contexto e recarrega os blocos. */
  onSelecionarCliente(cliente: ClienteContadorConsultaDTO | null): void {
    if (!cliente) {
      this.trocarCliente();
      return;
    }

    // Detecta TROCA (já havia um cliente) vs PRIMEIRA seleção (nenhum cliente antes).
    const trocaDeCliente = this.clienteSelecionado != null;

    this.clienteSelecionado = cliente;
    // Mantem o autocomplete exibindo o cliente escolhido (inclusive quando a
    // selecao vem de fora, como a pre-selecao por query param).
    this.clienteAutoComplete = cliente;
    this.contexto.selecionar(cliente); // liga o header X-Empresa-Contexto

    // Reinicia o estado dos blocos do cliente anterior.
    this.contaSelecionadaId = null;
    this.estadoSelecionado = null;
    this.notas = [];
    this.first = 0;
    this.totalRecords = 0;
    // Zera a assinatura para garantir que a 1a carga deste cliente sempre ocorra
    // (e reselecionar o mesmo cliente apos "voltar" tambem recarregue).
    this._ultimaAssinaturaNotas = null;

    // Na PRIMEIRA seleção (clienteSelecionado: null -> cliente) a seção é montada e o
    // lazy-load do p-table dispara onLazyLoadNotas automaticamente — não recarregamos aqui
    // para evitar carga dupla. Na TROCA (cliente -> cliente) o p-table é reutilizado e, como
    // `first` continua 0, o lazy-load NÃO re-dispara; por isso recarregamos explicitamente
    // (o contexto X-Empresa-Contexto já foi atualizado de forma síncrona acima).
    if (trocaDeCliente) {
      this.carregarNotas(0, this.rows);
    }
  }

  /** Volta ao seletor: desliga o contexto e limpa o estado dos blocos. */
  trocarCliente(): void {
    this.contexto.limpar();
    this.clienteSelecionado = null;
    this.clienteAutoComplete = null;
    this.contas = [];
    this.contaSelecionadaId = null;
    this.estadoSelecionado = null;
    this.notas = [];
    this.first = 0;
    this.totalRecords = 0;
    this._ultimaAssinaturaNotas = null;
  }

  // ===========================================================================
  // Extrato de caixa
  // ===========================================================================

  private carregarContas(): void {
    this.carregandoContas = true;

    this.operarService.listarContas().subscribe({
      next: (res) => {
        this.contas = res.content || [];
        this.carregandoContas = false;
      },
      error: (err) => {
        this.contas = [];
        this.carregandoContas = false;
        this.tratarErroContexto(err);
      },
    });
  }

  onPeriodoChanged(periodo: PeriodoFiltro): void {
    this.periodo = periodo;
    // O período filtra as notas; recarrega da primeira página.
    if (this.clienteSelecionado) {
      this.first = 0;
      this.carregarNotas(0, this.rows);
    }
  }

  /** Filtro por estado da nota eletronica: recarrega a lista da primeira pagina. */
  onEstadoChanged(): void {
    if (this.clienteSelecionado) {
      this.first = 0;
      this.carregarNotas(0, this.rows);
    }
  }

  gerarExtrato(): void {
    if (this.contaSelecionadaId == null) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador_operar.extrato.selecione_conta'),
      });
      return;
    }

    const params: ExtratoDeCaixaPdfParams = { conta: this.contaSelecionadaId };
    if (this.periodo?.dataInicio) {
      params.dataInicio = this.periodo.dataInicio;
    }
    if (this.periodo?.dataFim) {
      params.dataFim = this.periodo.dataFim;
    }

    this.gerandoPdf = true;
    this.operarService.gerarExtratoPdf(params).subscribe({
      next: (base64) => {
        this.gerandoPdf = false;
        if (base64 && base64.length > 0) {
          FileUtils.abrirPdf(base64, 'extrato-de-caixa.pdf');
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('contador_operar.extrato.sem_dados'),
          });
        }
      },
      error: (err) => {
        this.gerandoPdf = false;
        if (this.tratarErroContexto(err)) {
          return;
        }
        if (err?.status === 422) {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('contador_operar.extrato.selecione_conta'),
          });
        } else if (err?.status === 404) {
          this.messageService.add({
            severity: 'info',
            summary: this.translate.instant('contador_operar.extrato.sem_dados'),
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: err?.error?.message || this.translate.instant('contador_operar.mensagens.erro_generico'),
          });
        }
      },
    });
  }

  /**
   * Converte data no formato "dd/MM/yyyyTHH:mm" para "dd/MM/yyyy", que é o
   * formato aceito pelos endpoints /relatorios/livro-de-vendas e /nota/buscar.
   * Se a string não contiver 'T', retorna como está (defensive).
   */
  private soData(dataComHora: string): string {
    const idx = dataComHora.indexOf('T');
    return idx >= 0 ? dataComHora.substring(0, idx) : dataComHora;
  }

  /**
   * Monta os params do Livro de Vendas a partir dos filtros ativos da aba
   * (periodo + estado da nota eletronica). Compartilhado pelos 3 formatos.
   * As datas são convertidas de "dd/MM/yyyyTHH:mm" para "dd/MM/yyyy" pois o
   * endpoint /relatorios/livro-de-vendas valida sem hora.
   */
  private montarParamsLivro(): { dataInicio?: string; dataFim?: string; estadoNotaEletronica?: string; semEstadoEletronico?: boolean } {
    const params: { dataInicio?: string; dataFim?: string; estadoNotaEletronica?: string; semEstadoEletronico?: boolean } = {};
    if (this.periodo?.dataInicio) {
      params.dataInicio = this.soData(this.periodo.dataInicio);
    }
    if (this.periodo?.dataFim) {
      params.dataFim = this.soData(this.periodo.dataFim);
    }
    // "Não emitida" e "Não eletrônica" enviam semEstadoEletronico; os demais estados vao
    // como estadoNotaEletronica. O endpoint do Livro nao aceita tipoNota, entao as duas
    // exportam o mesmo conjunto (todas sem estado) — a distincao por tipo so existe na
    // tabela (/nota/buscar).
    if (
      this.estadoSelecionado === FILTRO_NAO_ELETRONICA ||
      this.estadoSelecionado === FILTRO_NAO_EMITIDA
    ) {
      params.semEstadoEletronico = true;
    } else if (this.estadoSelecionado) {
      params.estadoNotaEletronica = this.estadoSelecionado;
    }
    return params;
  }

  /**
   * Exporta o "Libro de Ventas" (PDF) do cliente respeitando os filtros ativos
   * da aba (periodo + estado da nota eletronica). Espelha gerarExtrato:
   * sucesso => abre o PDF; base64 vazio => "sem dados"; trata 403 de contexto, 422 e 404.
   * Acionado pela opcao "PDF" do menu popup "Exportar".
   */
  gerarLivroDeVendas(): void {
    const params = this.montarParamsLivro();

    this.gerandoLivro = true;
    this.operarService.gerarLivroDeVendasPdf(params).subscribe({
      next: (base64) => {
        this.gerandoLivro = false;
        if (base64 && base64.length > 0) {
          FileUtils.abrirPdf(base64, 'livro-de-vendas.pdf');
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('contador_operar.livro.sem_dados'),
          });
        }
      },
      error: (err) => {
        this.gerandoLivro = false;
        this.tratarErroLivro(err);
      },
    });
  }

  /**
   * Exporta o "Libro de Ventas" como arquivo binario (.xlsx ou .csv) do cliente,
   * respeitando os filtros ativos. Sucesso => baixa o blob; blob vazio
   * (size === 0) => "sem dados". Trata 403 de contexto, 422 e 404 como o PDF.
   */
  gerarLivroDeVendasArquivo(formato: 'xlsx' | 'csv'): void {
    const params = this.montarParamsLivro();
    const nomeArquivo = `livro-de-vendas.${formato}`;

    this.gerandoLivro = true;
    this.operarService.gerarLivroDeVendasArquivo(formato, params).subscribe({
      next: (blob) => {
        this.gerandoLivro = false;
        if (blob && blob.size > 0) {
          FileUtils.baixarBlob(blob, nomeArquivo);
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('contador_operar.livro.sem_dados'),
          });
        }
      },
      error: (err) => {
        this.gerandoLivro = false;
        this.tratarErroLivro(err);
      },
    });
  }

  /**
   * Tratamento de erro compartilhado pelos 3 formatos de export do Livro de
   * Vendas: 403 de contexto (vinculo perdido), 422/404 como "sem dados", e
   * fallback generico. Em erro de blob, `err.error` vem como Blob, mas
   * `err.status` permanece acessivel, entao o roteamento por status funciona.
   */
  private tratarErroLivro(err: any): void {
    if (this.tratarErroContexto(err)) {
      return;
    }
    if (err?.status === 422) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador_operar.livro.sem_dados'),
      });
    } else if (err?.status === 404) {
      this.messageService.add({
        severity: 'info',
        summary: this.translate.instant('contador_operar.livro.sem_dados'),
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: err?.error?.message || this.translate.instant('contador_operar.mensagens.erro_generico'),
      });
    }
  }

  // ===========================================================================
  // Notas (tabela lazy)
  // ===========================================================================

  /**
   * Monta o parametro `sort` (formato Spring "campo,asc|desc") a partir do
   * estado de ordenacao atual. Retorna undefined quando nenhum cabecalho foi
   * clicado — assim o backend mantem sua ordenacao padrao.
   */
  private montarSort(): string | undefined {
    if (!this.sortField || this.sortOrder === 0) {
      return undefined;
    }
    return `${this.sortField},${this.sortOrder === 1 ? 'asc' : 'desc'}`;
  }

  private carregarNotas(page: number, size: number): void {
    const sort = this.montarSort();

    // Dedupe: ignora a 2a chamada IDENTICA consecutiva (mesma assinatura).
    // Caso/motivo documentados em _ultimaAssinaturaNotas; zerada no erro abaixo
    // para nao bloquear o "tentar novamente". O `sort` entra na assinatura para
    // que trocar APENAS a ordenacao (mesma pagina/filtros) nao seja descartado.
    const assinatura = [
      this.clienteSelecionado?.empresaIndicadaId ?? '',
      page,
      size,
      this.periodo?.dataInicio ?? '',
      this.periodo?.dataFim ?? '',
      this.estadoSelecionado ?? '',
      sort ?? '',
    ].join('|');
    if (assinatura === this._ultimaAssinaturaNotas) {
      return;
    }
    this._ultimaAssinaturaNotas = assinatura;

    this.carregandoNotas = true;
    this.erroNotas = false;

    // "Não emitida" e "Não eletrônica" sao notas sem estado eletronico (NULL): ambas
    // enviam semEstadoEletronico; o TIPO da nota as separa — "Não emitida" adiciona
    // tipoNota=ELETRONICA (pendente de emissao). Demais estados vao em estadoNotaEletronica.
    const semEstadoEletronico =
      this.estadoSelecionado === FILTRO_NAO_ELETRONICA ||
      this.estadoSelecionado === FILTRO_NAO_EMITIDA;
    const estado = semEstadoEletronico ? undefined : (this.estadoSelecionado ?? undefined);
    const tipoNota =
      this.estadoSelecionado === FILTRO_NAO_EMITIDA ? TipoPadraoNota.ELETRONICA : undefined;

    this.operarService
      .listarNotas(
        page,
        size,
        this.periodo?.dataInicio ? this.soData(this.periodo.dataInicio) : undefined,
        this.periodo?.dataFim ? this.soData(this.periodo.dataFim) : undefined,
        estado,
        semEstadoEletronico,
        sort,
        tipoNota,
      )
      .subscribe({
        next: (res) => {
          this.notas = res.content || [];
          this.totalRecords = res.totalElements ?? 0;
          this.first = page * size;
          this.carregandoNotas = false;
        },
        error: (err) => {
          this.notas = [];
          this.totalRecords = 0;
          this.carregandoNotas = false;
          // Libera a assinatura para que o "tentar novamente" (recarregarNotas),
          // que repete os mesmos parametros, nao seja bloqueado pelo dedupe.
          this._ultimaAssinaturaNotas = null;
          if (this.tratarErroContexto(err)) {
            return;
          }
          // O backend lanca 404 quando nao ha notas no recorte/filtro: isso e
          // estado VAZIO (ex.: filtro de estado sem resultado), nao um erro.
          if (err?.status === 404) {
            return;
          }
          // Erro real exibido inline na seção de notas (sem toast/popup).
          this.erroNotas = true;
        },
      });
  }

  onLazyLoadNotas(event: TableLazyLoadEvent): void {
    if (!this.clienteSelecionado) {
      return;
    }

    // Tabela [lazy]: clicar num cabecalho `pSortableColumn` dispara o PROPRIO
    // onLazyLoad ja com sortField/sortOrder — por isso capturamos o sort aqui
    // (sem um (onSort) separado, que causaria recarga dupla). Em ordenacao
    // simples, sortField vem como string; sem ordenacao vem null/undefined.
    const novoSortField = typeof event.sortField === 'string' ? event.sortField : null;
    const novoSortOrder = novoSortField ? (event.sortOrder ?? 0) : 0;
    const sortMudou = novoSortField !== this.sortField || novoSortOrder !== this.sortOrder;

    const size = event.rows ?? this.rows;
    this.rows = size;

    // Ao mudar a ordenacao, volta para a primeira pagina e recarrega. O
    // loop-guard (dedupe por assinatura, que inclui o sort) absorve a
    // reemissao do onLazyLoad provocada pela reatribuicao de [value].
    if (sortMudou) {
      this.sortField = novoSortField;
      this.sortOrder = novoSortOrder;
      this.first = 0;
      this.carregarNotas(0, size);
      return;
    }

    const page = Math.floor((event.first ?? 0) / size);
    this.carregarNotas(page, size);
  }

  recarregarNotas(): void {
    const page = this.rows > 0 ? Math.floor(this.first / this.rows) : 0;
    this.carregarNotas(page, this.rows);
  }

  /**
   * A sub-aba "Nova venda" gravou uma nota. A listagem vive AQUI (no pai), por
   * isso o filho avisa via @Output.
   *
   * Sem isto a nota so aparecia depois de um F5, por DOIS motivos medidos em
   * runtime: (1) voltar da sub-aba "Nova venda" para a listagem NAO re-dispara o
   * lazy-load do p-table (o painel e reaproveitado e `first` continua 0), entao
   * zero requisicoes eram feitas; (2) mesmo que disparasse, o dedupe de
   * `carregarNotas` descartaria a chamada, ja que cliente/pagina/periodo/estado
   * continuam identicos — o que mudou foi o dado no servidor, nao o filtro.
   * Por isso a assinatura e invalidada explicitamente antes de recarregar.
   *
   * Volta para a primeira pagina: a nota nova entra no topo da ordenacao padrao.
   */
  onVendaConcluida(): void {
    if (!this.clienteSelecionado) {
      return;
    }
    this.first = 0;
    this._ultimaAssinaturaNotas = null;
    this.carregarNotas(0, this.rows);
  }

  /**
   * A transmissao da NFe se resolveu (autorizada, rejeitada ou com falha) e a
   * SITUACAO da nota mudou no servidor. Acontece somente no caminho de timbrado
   * eletronico — e a segunda recarga, depois da que `onVendaConcluida` ja fez
   * para a nota aparecer na lista.
   *
   * Recarrega a pagina ATUAL (e nao a primeira): a nota ja esta na listagem, o
   * que mudou foi a coluna "Situacao". Mesma invalidacao de dedupe do
   * `emitirFatura`: os filtros nao mudaram, so o dado.
   */
  onNotaTransmitida(): void {
    if (!this.clienteSelecionado) {
      return;
    }
    this._ultimaAssinaturaNotas = null;
    this.recarregarNotas();
  }

  /** Troca de pagina pelo paginador externo (mesmo modelo da Lista de Vendas).
   *  Como a tabela e lazy, recarregamos a pagina solicitada do backend. */
  onPageChangeNotas(event: { page?: number; rows?: number }): void {
    if (!this.clienteSelecionado) {
      return;
    }
    const size = event.rows ?? this.rows;
    const page = event.page ?? 0;
    this.rows = size;
    this._lastRowsNotas = size;
    this.carregarNotas(page, size);
  }

  /** Troca de "itens por pagina": volta para a primeira pagina e recarrega.
   *  Guarda contra o (ngModelChange) emitido no bootstrap com o valor atual. */
  onRowsPerPageChangeNotas(): void {
    if (this.rows === this._lastRowsNotas) {
      return;
    }
    this._lastRowsNotas = this.rows;
    if (!this.clienteSelecionado) {
      return;
    }
    this.first = 0;
    this.carregarNotas(0, this.rows);
  }

  /** Valor exibido da nota: prioriza o total com desconto, com fallback ao total. */
  valorNota(nota: NotaDTO): number {
    return nota.valorTotalComDesconto ?? nota.valorTotal ?? 0;
  }

  /**
   * Config de formatacao da moeda padrao da venda (simbolo/casas/locale).
   * Robusto: `moeda` pode vir como string ('GUARANI'), LocalizableEnum
   * ({ enum: 'GUARANI', ... }) ou ausente. Nunca retorna undefined
   * (fallback GUARANI) para nao quebrar o template com `.symbol`.
   */
  cfgMoeda(nota: NotaDTO): MoedaConfig {
    const bruto = nota.moeda as unknown;
    const codigo =
      bruto && typeof bruto === 'object'
        ? (bruto as { enum?: string }).enum
        : (bruto as string | undefined);
    return MOEDA_CONFIG[codigo as Moeda] ?? MOEDA_CONFIG[Moeda.GUARANI];
  }

  /**
   * Código do tipo da nota (ex.: ELETRONICA). `tipoNota` pode vir como
   * LocalizableEnum ({ enum, nome, ... }) ou string (retrocompat) — espelha
   * cfgMoeda/codigoEstado.
   */
  private tipoNotaValue(nota: NotaDTO | null): string {
    if (!nota) return '';
    const bruto = nota.tipoNota as unknown;
    return (bruto && typeof bruto === 'object'
      ? (bruto as LocalizableEnum).enum
      : (bruto as string)) ?? '';
  }

  /**
   * Rótulo localizado do tipo da nota: usa o `nome` do LocalizableEnum;
   * se vier como string (retrocompat), cai no i18n `enums.tipo_padrao_nota.*`.
   */
  tipoNotaLabel(nota: NotaDTO | null): string {
    if (!nota) return '';
    const bruto = nota.tipoNota as unknown;
    if (bruto && typeof bruto === 'object') {
      return (bruto as LocalizableEnum).nome || '';
    }
    const code = (bruto as string) || '';
    const chave = `enums.tipo_padrao_nota.${code}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? code : traduzido;
  }

  /** True quando a nota é fatura eletrônica (exibe o rótulo "Electrónica"). */
  ehNotaEletronica(nota: NotaDTO): boolean {
    return this.tipoNotaValue(nota) === TipoPadraoNota.ELETRONICA;
  }

  /**
   * Número da fatura no padrão Paraguai: filial-pontoExpedicao-sequencial
   * (ex.: 535-034-0000233). Retorna '' quando não há número de fatura.
   */
  numeroFaturaFormatado(nota: NotaDTO): string {
    if (!nota.numeroFatura) return '';
    const filial = (nota.numeroFilial ?? 0).toString().padStart(3, '0');
    const ponto = (nota.numeroPontoExpedicao ?? 0).toString().padStart(3, '0');
    const seq = nota.numeroFatura.toString().padStart(7, '0');
    return `${filial}-${ponto}-${seq}`;
  }

  /**
   * Situação exibida: estadoNotaEletronica tem prioridade sobre estadoNota.
   * Os campos são LocalizableEnum ({ enum, nome, descricao }) ou string (retrocompat);
   * extrai o `nome` (label localizado).
   */
  situacaoNota(nota: NotaDTO): string {
    const extrair = (estado: LocalizableEnum | string | undefined): string => {
      if (!estado) return '';
      return typeof estado === 'string' ? estado : (estado.nome || '');
    };
    return extrair(nota.estadoNotaEletronica) || extrair(nota.estadoNota) || '-';
  }

  /** Código do enum (ex.: AUTORIZADA) do estado, p/ definir a cor do tag. */
  private codigoEstado(estado: LocalizableEnum | string | undefined): string {
    if (!estado) return '';
    return (typeof estado === 'string' ? estado : estado.enum || '').toUpperCase();
  }

  /** Cor (severity do p-tag) conforme o estado da nota. */
  situacaoSeverity(nota: NotaDTO): 'success' | 'info' | 'warning' | 'danger' {
    const codigo = this.codigoEstado(nota.estadoNotaEletronica) || this.codigoEstado(nota.estadoNota);
    switch (codigo) {
      case 'AUTORIZADA':
      case 'APROVADO':
      case 'EFETIVADO':
        return 'success';
      case 'CANCELADA':
      case 'DENEGADA':
      case 'REJEITADA':
      case 'DEVOLVIDO':
        return 'danger';
      case 'COM_PROBLEMAS':
      case 'DEVOLVIDO_PARCIAL':
      case 'INUTILIZADA':
        return 'warning';
      default:
        return 'info';
    }
  }

  // ===========================================================================
  // Visualizar nota (tela de fatura sob contexto)
  // ===========================================================================

  /**
   * Abre a tela operacional da fatura (`/notas-list/fatura/:id`) com os dados do
   * cliente. O contexto sobrevive a navegacao (ver ContadorContextoService) e os
   * GET de leitura da tela estao no allowlist; ao voltar/sair do fluxo, o contexto
   * e limpo automaticamente.
   */
  visualizarNota(nota: NotaDTO): void {
    this.router.navigate(['/notas-list/fatura', nota.id]);
  }

  // ===========================================================================
  // Emitir fatura (transmitir NFe)
  // ===========================================================================

  /** Abre o popup com o card "Estado factura electrónica" da nota. */
  confirmarEmissao(nota: NotaDTO): void {
    this.notaEstadoNfe = nota;
    this.mostrarDialogEstadoNfe = true;
  }

  /** Transmite (emite) a NFe da nota aberta no popup. */
  transmitir(): void {
    // Nota nao eletronica nao tem o que transmitir ao SIFEN.
    if (this.notaEstadoNfe && this.ehNotaEletronica(this.notaEstadoNfe)) {
      this.emitirFatura(this.notaEstadoNfe);
    }
  }

  /** Codigo do estado da NFe (ex.: EM_DEFINICAO) — define badge e acoes. */
  estadoNfeValue(nota: NotaDTO | null): string {
    if (!nota) return 'EM_DEFINICAO';
    const estado = nota.estadoNotaEletronica;
    if (!estado) return 'EM_DEFINICAO';
    return (typeof estado === 'string' ? estado : estado.enum || 'EM_DEFINICAO').toUpperCase();
  }

  /** Cor (severity do p-tag) conforme o estado da NFe. */
  estadoNfeSeverity(nota: NotaDTO | null): 'success' | 'info' | 'warning' | 'danger' {
    // Sem estado eletronico (NULL): nota do tipo ELETRONICA = ainda nao emitida ao
    // SIFEN (pendente, destaque); fatura legal nao eletronica = neutro.
    if (nota && !nota.estadoNotaEletronica) {
      return this.ehNotaEletronica(nota) ? 'warning' : 'info';
    }
    switch (this.estadoNfeValue(nota)) {
      case 'AUTORIZADA':
      case 'APROVADO':
        return 'success';
      case 'CANCELADA':
      case 'INUTILIZADA':
        return 'danger';
      case 'COM_PROBLEMAS':
        return 'warning';
      default: // EM_DEFINICAO (ABIERTA) e demais
        return 'info';
    }
  }

  /** Rotulo localizado do estado da NFe (prioriza o `nome` do LocalizableEnum). */
  estadoNfeLabel(nota: NotaDTO | null): string {
    if (!nota) return '';
    const estado = nota.estadoNotaEletronica;
    // Sem estado eletronico (NULL), o rotulo depende do TIPO da nota:
    // - tipoNota=ELETRONICA: eletronica ainda NAO emitida ao SIFEN -> "Nao emitida"
    //   (evita colidir com o rotulo "Eletronica" da coluna Identificacao).
    // - demais tipos: fatura legal nao eletronica (autoimpressor/manual) -> "Nao eletronica".
    // Rotulos distintos de EM_DEFINICAO para alinhar o badge ao filtro de estado
    // (que casa apenas o EM_DEFINICAO literal).
    if (!estado) {
      return this.ehNotaEletronica(nota)
        ? this.translate.instant('contador_operar.notas.situacao_nao_emitida')
        : this.translate.instant('contador_operar.notas.situacao_nao_eletronica');
    }
    if (typeof estado !== 'string' && estado.nome) {
      return estado.nome;
    }
    const valor = this.estadoNfeValue(nota).toLowerCase();
    const chave = `enums.estado_nota_eletronica.${valor}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? this.estadoNfeValue(nota) : traduzido;
  }

  private emitirFatura(nota: NotaDTO): void {
    this.emitindoNotaId = nota.id;

    this.operarService.transmitirNfe(nota.id).subscribe({
      next: (resposta) => {
        this.emitindoNotaId = null;

        const descMensagem = (resposta?.descMensagem || '').trim().toUpperCase();
        const sucesso = ['AUTORIZADO', 'AUTORIZADA', 'APROVADO', 'APROVADA'].includes(descMensagem);

        if (sucesso) {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('contador_operar.emitir_nfe.sucesso_titulo'),
            detail: this.translate.instant('contador_operar.emitir_nfe.sucesso_detalhe'),
          });
          this.mostrarDialogEstadoNfe = false;
          this.notaEstadoNfe = null;
          // A emissao mudou a SITUACAO da nota no servidor, mas os filtros da
          // lista continuam identicos — sem invalidar o dedupe, o recarregar
          // abaixo seria descartado e a coluna "Situacao" so mudaria apos F5.
          // A invalidacao mora AQUI (ponto de escrita) e nao dentro de
          // `recarregarNotas`, que tambem serve ao botao "tentar novamente"
          // (leitura), onde o dedupe deve continuar valendo.
          this._ultimaAssinaturaNotas = null;
          this.recarregarNotas();
        } else {
          const mensagemRejeicao = this.extrairMensagemRejeicao(resposta);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('contador_operar.emitir_nfe.erro_titulo'),
            detail: mensagemRejeicao || this.translate.instant('contador_operar.emitir_nfe.erro_detalhe'),
          });
        }
      },
      error: (err) => {
        this.emitindoNotaId = null;
        if (this.tratarErroContexto(err)) {
          return;
        }
        if (err?.status === 404) {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('contador_operar.mensagens.nota_nao_encontrada'),
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('contador_operar.emitir_nfe.erro_titulo'),
            detail: err?.error?.message || this.translate.instant('contador_operar.emitir_nfe.erro_detalhe'),
          });
        }
      },
    });
  }

  /** Ultima mensagem de retorno da SIFEN (campo list[].descMensagem), se houver. */
  private extrairMensagemRejeicao(resposta: any): string | null {
    if (!resposta?.list || !Array.isArray(resposta.list) || resposta.list.length === 0) {
      return resposta?.descMensagem?.trim() || null;
    }
    const ultimoRetorno = resposta.list[resposta.list.length - 1];
    const mensagem = ultimoRetorno?.descMensagem?.trim();
    return mensagem && mensagem.length > 0 ? mensagem : null;
  }

  // ===========================================================================
  // 403 de contexto (vinculo contador <-> cliente perdido)
  // ===========================================================================

  /**
   * Trata o 403 de contexto: limpa o contexto, volta ao seletor e avisa.
   * Retorna true se o erro era 403 (ja tratado) — o chamador deve interromper.
   */
  private tratarErroContexto(err: any): boolean {
    if (err?.status !== 403) {
      return false;
    }
    this.contexto.limpar();
    this.clienteSelecionado = null;
    this.clienteAutoComplete = null;
    this.contas = [];
    this.contaSelecionadaId = null;
    this.estadoSelecionado = null;
    this.notas = [];
    this.first = 0;
    this.totalRecords = 0;
    this.messageService.add({
      severity: 'warn',
      summary: this.translate.instant('contador_operar.mensagens.vinculo_perdido_titulo'),
      detail: this.translate.instant('contador_operar.mensagens.vinculo_perdido_detalhe'),
    });
    return true;
  }
}
